import {useMemo, useState, type ReactNode} from 'react'
import {Card, CardContent} from '@/components/ui/card'
import {Input} from '@/components/ui/input'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {useI18n} from '@/i18n'
import type {TypeValue} from '@/types/types'

const UNGROUPED_KEY = '__ungrouped__'

interface TypeValuesViewProps {
    values: TypeValue[]
    locales: string[]
    isEntityType: boolean
    hasUsageTracking: boolean
    renderRow: (value: TypeValue) => ReactNode
}

export function TypeValuesView({values, locales, isEntityType, hasUsageTracking, renderRow}: TypeValuesViewProps) {
    const grouped = values.some((v) => v.displayGroup != null && v.displayGroup.length > 0)
    if (!grouped) {
        return <FlatView values={values} isEntityType={isEntityType} hasUsageTracking={hasUsageTracking} renderRow={renderRow}/>
    }
    return (
        <GroupedView
            values={values}
            locales={locales}
            isEntityType={isEntityType}
            hasUsageTracking={hasUsageTracking}
            renderRow={renderRow}
        />
    )
}

function FlatView({values, isEntityType, hasUsageTracking, renderRow}: Omit<TypeValuesViewProps, 'locales'>) {
    return (
        <Card>
            <CardContent className="p-0">
                <Table>
                    <ValueColumnHeader isEntityType={isEntityType} hasUsageTracking={hasUsageTracking}/>
                    <TableBody>{values.map(renderRow)}</TableBody>
                </Table>
            </CardContent>
        </Card>
    )
}

function GroupedView({values, locales, isEntityType, hasUsageTracking, renderRow}: TypeValuesViewProps) {
    const i18n = useI18n()
    const [search, setSearch] = useState('')
    const allGroups = useMemo(() => groupValues(values), [values])
    const allGroupKeys = useMemo(() => Array.from(allGroups.keys()), [allGroups])
    const [userExpanded, setUserExpanded] = useState<string[]>(allGroupKeys)

    const visibleEntries = useMemo(() => {
        if (search.length === 0) return Array.from(allGroups.entries())
        const out: Array<[string, TypeValue[]]> = []
        for (const [key, rows] of allGroups) {
            const matches = rows.filter((r) => matchesQuery(r, search, locales))
            if (matches.length > 0) out.push([key, matches])
        }
        return out
    }, [allGroups, search, locales])

    const visibleKeys = visibleEntries.map(([k]) => k)
    // WHY: during search, override user's collapsed state — otherwise a row
    // matching inside a collapsed group would be hidden behind the trigger.
    const expanded = search.length === 0 ? userExpanded : visibleKeys

    return (
        <Card>
            <div className="border-b p-3">
                <Input
                    data-testid="type-values-search"
                    placeholder={i18n.types.groupedView.searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <CardContent className="p-0">
                {visibleEntries.length === 0 ? (
                    <InlineEmptyText data-testid="type-values-no-results">
                        {i18n.types.groupedView.noResults}
                    </InlineEmptyText>
                ) : (
                    <Accordion
                        type="multiple"
                        value={expanded}
                        onValueChange={setUserExpanded}
                    >
                        {visibleEntries.map(([key, rows]) => {
                            const label =
                                key === UNGROUPED_KEY
                                    ? i18n.types.groupedView.ungroupedLabel
                                    : key
                            return (
                                <AccordionItem
                                    key={key}
                                    value={key}
                                    data-testid={`group-${key}`}
                                    className="border-b last:border-b-0"
                                >
                                    <AccordionTrigger className="px-4">
                                        <span className="flex items-center gap-2">
                                            <span data-testid={`group-label-${key}`}>
                                                {label}
                                            </span>
                                            <Badge
                                                variant="outline"
                                                data-testid={`group-count-${key}`}
                                            >
                                                {rows.length}
                                            </Badge>
                                        </span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-0">
                                        <Table>
                                            <ValueColumnHeader isEntityType={isEntityType} hasUsageTracking={hasUsageTracking}/>
                                            <TableBody>{rows.map(renderRow)}</TableBody>
                                        </Table>
                                    </AccordionContent>
                                </AccordionItem>
                            )
                        })}
                    </Accordion>
                )}
            </CardContent>
        </Card>
    )
}

function ValueColumnHeader({isEntityType, hasUsageTracking}: {isEntityType: boolean; hasUsageTracking: boolean}) {
    const i18n = useI18n()
    return (
        <TableHeader>
            <TableRow>
                {isEntityType && <TableHead data-testid="col-icon" className="w-10"/>}
                <TableHead data-testid="col-code">{i18n.types.code}</TableHead>
                {hasUsageTracking && (
                    <TableHead data-testid="col-usage" className="text-center">
                        {i18n.types.usage}
                    </TableHead>
                )}
                <TableHead data-testid="col-active" className="text-center">
                    {i18n.common.active}
                </TableHead>
                <TableHead data-testid="col-actions" className="w-12"/>
            </TableRow>
        </TableHeader>
    )
}

function groupValues(values: readonly TypeValue[]): Map<string, TypeValue[]> {
    const groups = new Map<string, TypeValue[]>()
    for (const v of values) {
        const key =
            v.displayGroup != null && v.displayGroup.length > 0
                ? v.displayGroup
                : UNGROUPED_KEY
        const bucket = groups.get(key)
        if (bucket) {
            bucket.push(v)
        } else {
            groups.set(key, [v])
        }
    }
    for (const bucket of groups.values()) {
        bucket.sort((a, b) => a.code.localeCompare(b.code))
    }
    return new Map(
        Array.from(groups.entries()).sort(([a], [b]) => {
            if (a === UNGROUPED_KEY) return 1
            if (b === UNGROUPED_KEY) return -1
            return a.localeCompare(b)
        }),
    )
}

function matchesQuery(value: TypeValue, query: string, locales: readonly string[]): boolean {
    const q = query.toLowerCase()
    if (value.code.toLowerCase().includes(q)) return true
    for (const locale of locales) {
        const label = value.translations[locale]?.label
        if (label != null && label.toLowerCase().includes(q)) return true
    }
    return false
}
